import { createI18n } from "vue-i18n";
import en from "./local/en.json";
import ar from "./local/ar.json";
import fr from "./local/fr.json";
import es from "./local/es.json";

/**
 * Locale source of truth.
 *
 * Resolution order at boot:
 *   1. localStorage value (the user's previous choice)
 *   2. browser language
 *   3. French (Morocco business default)
 *
 * `setLocale()` is the only correct way to change the active language —
 * it updates vue-i18n + persists + flips `<html lang>` and `dir`. RTL is
 * derived from the locale set below (just `ar` today).
 */

const STORAGE_KEY = "scap-locale";
const SUPPORTED = ["fr", "en", "ar", "es"];
const RTL_LOCALES = new Set(["ar"]);

function detectLanguage() {
  if (typeof localStorage !== "undefined") {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved && SUPPORTED.includes(saved)) return saved;
    } catch { /* private mode — fall through */ }
  }
  const browser = (navigator.language || navigator.userLanguage || "fr")
    .split("-")[0];
  return SUPPORTED.includes(browser) ? browser : "fr";
}

function applyHtmlAttrs(lang) {
  if (typeof document === "undefined") return;
  document.documentElement.lang = lang;
  // RTL block-level mirroring is intentionally deferred until every page
  // has been audited and fixed for RTL (header alignment, grids, icons,
  // borders, paddings). Until that design pass happens, we keep dir=ltr
  // even for Arabic — text still renders right-to-left within lines via
  // the Unicode bidi algorithm, so Arabic content reads correctly. To
  // re-enable full RTL later, change this back to:
  //   document.documentElement.dir = RTL_LOCALES.has(lang) ? "rtl" : "ltr";
  document.documentElement.dir = "ltr";
}

const initialLocale = detectLanguage();
applyHtmlAttrs(initialLocale);

const i18n = createI18n({
  legacy: false,
  locale: initialLocale,
  fallbackLocale: "fr",
  messages: { en, ar, fr, es },
});

/** Change the active language. Call this from the language switcher.
 *
 *  In addition to switching the UI locale, this dispatches a global
 *  `scap:locale-changed` window event so views that hold backend data
 *  (category names, product names — anything served localized by the
 *  Laravel SetLocale middleware) can refetch with the new locale. Pages
 *  subscribe in their `mounted()` hook:
 *
 *    window.addEventListener('scap:locale-changed', () => this.fetchAll());
 */
export function setLocale(lang) {
  if (!SUPPORTED.includes(lang)) return;
  const previous = i18n.global.locale.value;
  if (previous === lang) return;

  i18n.global.locale.value = lang;
  applyHtmlAttrs(lang);
  if (typeof localStorage !== "undefined") {
    try { localStorage.setItem(STORAGE_KEY, lang); } catch { /* ignore */ }
  }

  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("scap:locale-changed", {
      detail: { previous, current: lang },
    }));
  }
}

export const supportedLocales = SUPPORTED;
export const isRtl = (lang) => RTL_LOCALES.has(lang);
export default i18n;
